import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
    getProductsForCompare,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CompareActive from "@tIntermatic/Icons/CompareActive";
import CompareDefault from "@tIntermatic/Icons/CompareDefault";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showCompare: boolean;
    extendedStyles?: ProductListProductImageStyles;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
    compareProducts: getProductsForCompare(state),
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface ProductListProductImageStyles {
    wrapper?: InjectableCss;
    checkboxLabel?: InjectableCss;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
}

export const productImageStyles: ProductListProductImageStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            span[role="checkbox"] {
                height: 19px;
                width: 19px;
                min-width: 19px !important;
                svg {
                    height: 19px;
                    width: 19px;
                }
            }
        `,
    },

    checkBox: {
        iconProps: { src: CompareDefault },
        css: css`
            align-self: center;
            padding-top: 20px;
            span[class^="IconWrapper"] {
                &:focus {
                    box-shadow: none;
                }
            }
        `,
    },
    checkBoxSelected: {
        iconProps: { src: CompareActive },
        css: css`
            align-self: center;
            padding-top: 20px;
            span[class^="IconWrapper"] {
                &:focus {
                    box-shadow: none;
                }
            }
        `,
    },
    checkboxLabel: {
        css: css`
            letter-spacing: 0;
            font-size: 12px;
            font-weight: 500;
            line-height: 14px;
            text-transform: uppercase;
            color: #999999;
        `,
    },
};

const INTProductCompare: FC<Props> = ({
    productContext,
    showCompare,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
    compareProducts,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));
    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && compareProducts.length > 2) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };
    return (
        <StyledWrapper
            {...styles.wrapper}
            data-test-selector="productListProductImage"
            data-test-key={productContext.product.id}
        >
            {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                <Checkbox
                    {...(productCompareChecked ? styles.checkBoxSelected : styles.checkBox)}
                    checked={productCompareChecked}
                    onChange={handleChanged}
                    data-test-selector="product-compare"
                    disabled={!productCompareChecked && compareProducts.length > 2}
                >
                    <StyledWrapper {...styles.checkboxLabel}>{translate("Compare")}</StyledWrapper>
                </Checkbox>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(INTProductCompare));
